<script setup lang="ts">
import heroBannersMock from './mocks'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Autoplay } from 'swiper/modules'
import type { Swiper as TSwiper } from 'swiper'
import { WidgetHeroBanner } from '~/components/widget/hero-banner'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import type { IHeroBanner } from '~/components/widget/hero-banner/types'

const config = useRuntimeConfig()
const heroBanners: IHeroBanner[] = heroBannersMock

// The ad goes between the first banner and the rest
type THeroSlide = { key: string; label: string; banner: IHeroBanner | null }

const slides = computed<THeroSlide[]>(() => {
  const banners = heroBanners.map((banner, index) => ({
    key: `hero-banner-${index}`,
    label: banner.tab ?? banner.heading,
    banner
  }))
  const ad = config.public.reviveId
    ? [{ key: 'hero-ad', label: 'widget.hero_banner.tab_ad', banner: null }]
    : []

  return [...banners.slice(0, 1), ...ad, ...banners.slice(1)]
})

// Progress indicator inside the slide replaces the pagination dots
const swiper = ref<TSwiper>()
const activeIndex = ref(0)

const onSwiper = (instance: TSwiper) => (swiper.value = instance)
const onSlideChange = (instance: TSwiper) => (activeIndex.value = instance.realIndex)

// All slides are the same height: 359px on tablets, the ad banner's proportions (1920x500) from xl
const slideMinHeightClass =
  'h-full min-h-[calc(100vw*0.865)] min-[360px]:min-h-[calc(100vw*0.856)] min-[375px]:min-h-[calc(100vw*0.860)] min-[390px]:min-h-[calc(100vw*0.862)] min-[412px]:min-h-[calc(100vw*0.866)] min-[430px]:min-h-[calc(100vw*0.868)] md:h-[359px] md:min-h-0 xl:aspect-[1920/500] xl:h-auto'
</script>

<template>
  <div v-if="heroBanners || config.public.reviveId" class="group relative">
    <Swiper
      :slides-per-view="1"
      :space-between="16"
      :modules="[Autoplay]"
      :autoplay="{ delay: 6000, pauseOnMouseEnter: true }"
      :loop="true"
      @swiper="onSwiper"
      @slide-change="onSlideChange"
    >
      <template v-for="(slide, index) in slides" :key="slide.key">
        <SwiperSlide v-if="slide.banner">
          <WidgetHeroBanner
            :intro="$t(slide.banner.intro)"
            :heading="$t(slide.banner.heading)"
            :text="$t(slide.banner.text)"
            :image="slide.banner.image ? useAsset(slide.banner.image) : null"
            :action-btn="slide.banner.actionBtn"
            :additional-action-btn="slide.banner.additionalActionBtn"
            :illustration="slide.banner.illustration"
            :backdrop="slide.banner.backdrop"
            :high-priority="index === 0"
            :class="slideMinHeightClass"
          />
        </SwiperSlide>

        <SwiperSlide
          v-else
          class="[&_img]:rounded-md [&_img]:border-2 [&_img]:border-blue-50 md:[&_img]:rounded-2xl"
        >
          <ClientOnly>
            <div :class="slideMinHeightClass">
              <WidgetReviveAd v-if="$device.isMobile" zoneid="32" />
              <WidgetReviveAd v-else zoneid="24" class="h-full" />
            </div>
          </ClientOnly>
        </SwiperSlide>
      </template>
    </Swiper>

    <div class="absolute inset-x-0 bottom-2 z-10 flex justify-center gap-1 md:bottom-3">
      <button
        v-for="(slide, index) in slides"
        :key="slide.key"
        type="button"
        :aria-label="$t(slide.label)"
        :aria-current="activeIndex === index"
        class="flex h-6 items-center rounded-full px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
        @click="swiper?.slideToLoop(index)"
      >
        <span
          class="block h-1 overflow-hidden rounded-full bg-blue-200 transition-all"
          :class="activeIndex === index ? 'w-10' : 'w-5'"
        >
          <!-- Same duration as the autoplay delay; pauses together with autoplay on hover -->
          <span
            v-if="activeIndex === index"
            :key="`progress-${activeIndex}`"
            class="block h-full origin-left animate-hero-tab-progress rounded-full bg-blue-600 group-has-[.swiper:hover]:[animation-play-state:paused] motion-reduce:animate-none"
          />
        </span>
      </button>
    </div>
  </div>
  <div v-else class="h-[260px] rounded-lg bg-blue-50 sm:h-[326px] md:rounded-2xl lg:h-[360px]" />
</template>
